import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  signal
} from '@angular/core';
import { fromEvent, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-detail-sidebar',
  templateUrl: './detail-sidebar.component.html',
  styleUrl: './detail-sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class DetailSidebarComponent implements AfterViewInit, OnChanges, OnDestroy {
  sidebarExpanded = signal<boolean>(false);
  sidePanelVisible = signal<boolean>(false);
  sidebarHovered = signal<boolean>(false);

  @Input() sidebarVisible: boolean = false;
  @Input() loading: boolean = false;
  @Output() sidebarVisibleChange = new EventEmitter<boolean>();
  @Output() hoverSidebar = new EventEmitter<boolean>();

  @ViewChild('sidepanel') sidepanel: ElementRef;
  @ContentChild('sidebarContent') content: TemplateRef<Element>;

  private readonly destroyed$: Subject<boolean> = new Subject<boolean>();
  private readonly LG = 992;

  get sidepanelIcon(): string {
    return this.sidebarExpanded() ? 'fa-regular fa-arrow-left' : 'fa-regular fa-arrow-right';
  }

  ngOnChanges(changes: SimpleChanges): void {
    const sidebarVisibleValue = changes['sidebarVisible'];

    if (sidebarVisibleValue) {
      if (sidebarVisibleValue?.currentValue && window.innerWidth >= this.LG) {
        this.sidebarExpanded.set(true);
      } else {
        this.sidebarExpanded.set(false);
      }
    }
  }

  ngAfterViewInit(): void {
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        if (window.innerWidth < this.LG) {
          this.sidebarExpanded.set(false);
          this.sidebarHovered.set(false);
        } else if (!this.sidebarExpanded() && this.sidebarVisible) {
          this.sidebarExpanded.set(true);
        }
      });
  }

  handleHoverSidebar(hover: boolean) {
    if (this.sidebarExpanded() || window.innerWidth < this.LG) {
      return;
    }

    this.sidebarHovered.set(hover);

    if (hover) {
      this.sidepanel?.nativeElement?.classList.add('absolute');
      this.hoverSidebar.emit(hover);
    } else {
      setTimeout(() => {
        this.sidepanel?.nativeElement?.classList.remove('absolute');
        this.hoverSidebar.emit(hover);
      }, 200);
    }
  }

  handleToggleSidebar() {
    if (window.innerWidth < this.LG) {
      this.sidePanelVisible.set(true);
    } else {
      this.sidebarVisibleChange.emit(!this.sidebarExpanded());
      this.sidebarExpanded.set(!this.sidebarExpanded());
      this.sidebarHovered.set(false);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
